import { useCallback, useEffect, useRef, type RefObject } from 'react'
import * as THREE from 'three'
import {
  getScenePending,
  subscribeScenePending,
} from './three/sceneReadiness'

// Clarity session replay records the DOM only — WebGL canvas pixels are never
// captured, so the 3D viewer shows up as an empty box in every recording.
// This hook mirrors the rendered frame into a small <img> that sits behind the
// canvas: invisible to real users (the live canvas paints over it, and the PNG
// keeps the same alpha as the canvas), but visible in Clarity replays where
// the canvas is blank.
const SNAPSHOT_MAX_WIDTH = 320
const CAPTURE_DEBOUNCE_MS = 800

interface ClarityCanvasMirrorRefs {
  rendererRef: RefObject<THREE.WebGLRenderer | null>
  sceneRef: RefObject<THREE.Scene | null>
  cameraRef: RefObject<THREE.Camera | null>
  imgRef: RefObject<HTMLImageElement | null>
}

export function useClarityCanvasMirror({
  rendererRef,
  sceneRef,
  cameraRef,
  imgRef,
}: ClarityCanvasMirrorRefs) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const capture = useCallback(() => {
    // Skip all work when Clarity isn't loaded on this page view
    if (typeof window === 'undefined' || !window.clarity) return

    const renderer = rendererRef.current
    const scene = sceneRef.current
    const camera = cameraRef.current
    const img = imgRef.current
    if (!renderer || !scene || !camera || !img) return

    try {
      // frameloop="demand" + preserveDrawingBuffer=false: the buffer is only
      // readable right after an explicit render
      renderer.render(scene, camera)

      const source = renderer.domElement
      if (!source.width || !source.height) return

      const scale = Math.min(1, SNAPSHOT_MAX_WIDTH / source.width)
      const offscreen = document.createElement('canvas')
      offscreen.width = Math.round(source.width * scale)
      offscreen.height = Math.round(source.height * scale)
      const ctx = offscreen.getContext('2d')
      if (!ctx) return

      ctx.drawImage(source, 0, 0, offscreen.width, offscreen.height)
      // WebP keeps the canvas alpha (transparent areas show the page
      // background in both the live page and the replay) at ~10x smaller
      // payload than PNG; browsers without WebP encoding return PNG instead
      img.src = offscreen.toDataURL('image/webp', 0.6)
      img.style.visibility = 'visible'
    } catch {
      // Best-effort mirror — never let it break the viewer
    }
  }, [rendererRef, sceneRef, cameraRef, imgRef])

  // Hide the (now stale) snapshot immediately and re-capture once the scene
  // settles. Called on camera movement and any config change.
  const scheduleCapture = useCallback(() => {
    const img = imgRef.current
    if (img) img.style.visibility = 'hidden'

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(capture, CAPTURE_DEBOUNCE_MS)
  }, [capture, imgRef])

  // Re-capture when async scene work (GLB/texture loads) drains — covers the
  // initial load and config changes that swap models
  useEffect(() => {
    const unsubscribe = subscribeScenePending(() => {
      if (getScenePending() === 0) scheduleCapture()
    })
    scheduleCapture()

    return () => {
      unsubscribe()
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [scheduleCapture])

  return scheduleCapture
}
